<template>
  <div class="table-container">
    <table>
      <thead>
        <tr>
          <th />
          <th v-for="(day, idx) in days" :key="idx" @mouseup="selectOff">
            {{ day }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(time, i) in times" :key="i">
          <td class="time" @mouseup="selectOff">
            {{ time }}
          </td>
          <td
            v-for="(t, j) in days"
            :key="j"
            class="cell"
            :class="getClass(i, j) ? 'active' : ''"
            @mousedown="cellHandler(i, j, true)"
            @mouseover="cellHandler(i, j)"
            @mouseup="selectOff"
          />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import constants from '@/dependencies/constants'
import ShortWeekDaysMixin from '@/dependencies/mixins/short-week-days-names'
import objUnbinder from '@/dependencies/helpers/object-unbinder'

export default {
  name: 'ScheduleTable',
  mixins: [ShortWeekDaysMixin],
  props: {
    scheduleObj: {
      type: [Object, Array],
      default: () => {}
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      times: constants.TIMES,
      scheduleMap: [],
      referenceArr: [],
      STEP: 60,
      MAX_LENGTH: 18,
      START: 360,
      END: 1440,
      schedule: {},
      values: '',
      selection: false
    }
  },

  watch: {
    values(newVal) {
      this.$emit('input', newVal)
    },
    scheduleMap: {
      deep: true,
      handler() {
        this.scheduleConverter()
        this.scheduleToStrConverter()
        this.$emit('change', this.schedule)
      }
    }
  },

  created() {
    this.schedulePropsToData()
    this.scheduleMapCreator()
    this.referenceArrCreator()
    this.scheduleToStrConverter()
    this.scheduleHandler()
  },
  mounted() {
    this.$bus.$on('clear.schedule', this.clearHandler)
  },
  beforeDestroy() {
    this.$bus.$off('clear.schedule')
  },

  methods: {
    scheduleMapCreator() {
      this.scheduleMap = objUnbinder(new Array(7).fill(new Array(18).fill(0)))
    },
    referenceArrCreator() {
      for (let i = this.START; i < this.END; i += this.STEP) {
        this.referenceArr.push(i)
      }
    },

    schedulePropsToData() {
      this.schedule = Array.isArray(this.scheduleObj)
        ? {}
        : { ...this.scheduleObj }
    },

    scheduleHandler() {
      if (!this.schedule || Array.isArray(this.schedule)) return

      for (const key in this.schedule) {
        this.scheduleMap[key - 1] = Object.assign(
          this.scheduleMap[key - 1],
          this.transformTimeFunc(this.schedule[key])
        )
      }
    },

    transformTimeFunc(_period) {
      const temp_arr = []
      let res = []

      _period.forEach((time) => {
        for (let i = time[0]; i < time[1]; i += this.STEP) {
          temp_arr.push(i)
        }
      })

      if (this.MAX_LENGTH !== temp_arr.length) {
        this.referenceArr.map((el) =>
          temp_arr.includes(el) ? res.push(1) : res.push(0)
        )
      } else {
        res = temp_arr.map(() => 1)
      }
      return res
    },

    scheduleConverter() {
      if (!this.scheduleObj) return
      this.scheduleMap.forEach((el, i) => {
        const res = []
        let temp_arr = []
        el.map((day, idx) => {
          if (day && temp_arr.length) {
            temp_arr.push(this.referenceArr[idx])
          } else if (day) {
            temp_arr.push(this.referenceArr[idx])
            res.push(temp_arr)
          } else {
            temp_arr = []
          }
        })

        res.forEach((el) => {
          const last = el[el.length - 1]
          el.push(last + this.STEP)
          el.splice(1, el.indexOf(last))
        })

        if (res.length) {
          this.schedule[i + 1] = res
        } else {
          delete this.schedule[i + 1]
        }
      })
    },

    scheduleToStrConverter() {
      if (!this.editable) return

      const convertedObj = {}
      const timeArr = []

      for (const key in this.schedule) {
        convertedObj[this.days[key - 1]] = this.schedule[key]
          .map((el) =>
            el.map((elem, idx) =>
              idx === 0
                ? this.times[this.referenceArr.indexOf(elem)]
                : this.times[this.referenceArr.indexOf(elem - this.STEP)]
            )
          )
          .map((elem) => [...new Set(elem)])
      }

      for (const key in convertedObj) {
        const timeString = convertedObj[key].map((el) => el.join('-'))

        timeArr.push(`${key}:${timeString}`)
      }
      this.values = timeArr.join('; ')
    },

    getClass(i, j) {
      return !!this.scheduleMap[j][i]
    },

    selectOff() {
      this.selection = false
    },

    cellHandler(i, j, _mass) {
      if (!this.editable) return

      switch (_mass) {
        case true:
          this.selection = true
          break
        case false:
          this.selection = false
          break
      }

      if (!this.selection) return
      this.$set(this.scheduleMap[j], i, +!this.scheduleMap[j][i])
    },

    clearHandler() {
      if (!this.editable) return

      this.schedule = {}
      this.scheduleMapCreator()
    }
  }
}
</script>

<style scoped lang="scss">
@import '~assets/css/variables';

table {
  width: 100%;
  height: 340px;
  text-align: center;
  background-color: #fff;

  thead {
    @include no-select();

    th {
      font-size: 11px;
      font-weight: bold;
      color: $gray;
      width: 12.5%;
      border: 1px solid #f0f0f0;
      line-height: 16px;
    }
  }

  tbody {
    @include no-select();

    .time {
      font-size: 11px;
      color: $gray;
      border: 1px solid #f0f0f0;
      line-height: 16px;
      text-align: right;
    }
    .cell {
      border: 1px solid #f0f0f0;
      background-color: #e0e0e0;
      font-size: 12px;

      &.active {
        background-color: $primary;
      }
    }
  }
}

.table-container {
  width: 100%;
  border-radius: 3px;
  box-shadow: 0 1px 9px rgba(0, 0, 0, 0.4);
}

.tab-content {
  .works-block {
    .scheduler {
      .table-container {
        z-index: 9;
        position: absolute;
      }
    }
  }
}
</style>
